import {
  BranchCommitEntityChangeDto,
  BranchTimelineEntryDto,
  BranchMilestoneDto,
  BranchRevisionStateDto,
  RestoreSnapshotRequestDto,
  RevertToPointRequestDto
} from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import { ENTITY_TYPE_DISPLAY_NAMES } from "@kuika/kuika-cl-model/src/enums/entity-type";
import React from "react";
import BranchingService from "../../../services/branching-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import KuikaButton, { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import { resolveCurrentCommitId } from "./branch-timeline-helpers";
import RestoreCommitModal from "./restore-commit-modal";
import "./branching-revert-timeline.scss";

// A destructive restore/rewind awaiting user confirmation.
type PendingRestore =
  | { mode: "restore"; snapshotId: string; label: string }
  | { mode: "rewind"; epochNo: number; targetSeqNo: number; label: string };

function formatRelativeTime(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString("en-US");
}

function truncateText(text: string, max: number): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

// Entity-type enum names → friendly singular labels for the commit history. Accepts either the enum
// name ("KWorkFlow") or its numeric value, so it's robust to how the change was serialized.
const ENUM_NAME_TO_FRIENDLY: Record<string, string> = {
  kaction: "Action",
  kdatasource: "Datasource",
  kproject: "Project",
  kscreendesign: "Screen",
  kworkflow: "Workflow",
  kapimanagement: "API",
  knotificationtemplate: "Notification",
  kscheduledjobs: "Scheduled Job",
  kofflinetable: "Offline Table",
  knotificationlanguagetemplate: "Notification Language"
};

function friendlyEntityTypeName(entityType?: string): string {
  const raw: string = (entityType ?? "").trim();
  if (!raw) return "Item";
  const asNumber: number = Number(raw);
  if (!Number.isNaN(asNumber) && ENTITY_TYPE_DISPLAY_NAMES[asNumber]) {
    return ENTITY_TYPE_DISPLAY_NAMES[asNumber];
  }
  return ENUM_NAME_TO_FRIENDLY[raw.toLowerCase()] ?? raw;
}

function pluralizeType(type: string, count: number): string {
  return count === 1 ? type : `${type}s`;
}

function changeVerb(changeType?: string): string {
  switch ((changeType ?? "").toLowerCase()) {
    case "add":
    case "added":
    case "a":
      return "Added";
    case "remove":
    case "removed":
    case "delete":
    case "deleted":
    case "d":
      return "Removed";
    case "update":
    case "updated":
    case "modified":
    case "m":
      return "Updated";
    default:
      return "Changed";
  }
}

function formatChangeSummary(change: BranchCommitEntityChangeDto): string {
  const verb: string = changeVerb(change.changeType);
  const type: string = friendlyEntityTypeName(change.entityType);
  let line = `${verb} ${type.toLowerCase()}`;
  // summaryJson is "{}" (placeholder) for most commits today; only append when it carries real detail.
  const summary: string = (change.summaryJson ?? "").trim();
  if (summary && summary !== "{}") {
    line += `: ${truncateText(summary, 120)}`;
  }
  return line;
}

function parseMessageParts(message?: string): { summary: string; description?: string } {
  if (!message) return { summary: "Commit" };
  const idx = message.indexOf("\n");
  if (idx === -1) return { summary: message };
  const summary = message.substring(0, idx).trim();
  const description = message.substring(idx + 1).trim();
  return { summary: summary || "Commit", description: description || undefined };
}

function formatChangeSummaryLine(changes: BranchCommitEntityChangeDto[]): string {
  if (!changes.length) return "No changes recorded";
  const counts: Record<string, number> = {};
  for (const c of changes) {
    const label = friendlyEntityTypeName(c.entityType);
    counts[label] = (counts[label] || 0) + 1;
  }
  return Object.entries(counts)
    .map(([type, count]) => `${count} ${pluralizeType(type, count).toLowerCase()}`)
    .join(", ");
}

interface IBranchingRevertTimelineProps {
  branchId: string;
  setLoading?: (value: boolean) => void;
  onAfterRevert?: () => void;
}

interface IBranchingRevertTimelineState {
  isLoading: boolean;
  hasLoaded: boolean;
  loadError: string | null;
  revisionState?: BranchRevisionStateDto;
  selectedCommit: BranchTimelineEntryDto | null;
  expandedCommitKeys: string[];
  branchTimeline: BranchTimelineEntryDto[];
  pendingRestore: PendingRestore | null;
  /** Where the restore/rewind confirmation's commit stands. Owned by `SaveStatus`. */
  restoreStatus: KuikaButtonStatus;
}

class BranchingRevertTimeline extends React.Component<IBranchingRevertTimelineProps, IBranchingRevertTimelineState> {
  state: IBranchingRevertTimelineState = {
    isLoading: false,
    hasLoaded: false,
    loadError: null,
    revisionState: undefined,
    selectedCommit: null,
    expandedCommitKeys: [],
    branchTimeline: [],
    pendingRestore: null,
    restoreStatus: "idle"
  };

  /**
   * The restore/rewind confirmation's state machine: the dialog stays up while the branch is
   * restored, holds its ✓, then closes onto the refreshed history.
   */
  private readonly restoreStatus: SaveStatus = new SaveStatus((restoreStatus: KuikaButtonStatus): void => {
    this.setState({ restoreStatus });
  });

  componentDidMount(): void {
    this.loadCommitsContext();
  }

  componentWillUnmount(): void {
    this.restoreStatus.dispose();
  }

  componentDidUpdate(prevProps: IBranchingRevertTimelineProps): void {
    if (prevProps.branchId !== this.props.branchId) {
      this.loadCommitsContext();
    }
  }

  // The branch whose data is currently held in state. Lets the loader tell a same-branch refresh
  // (keep showing content while reloading) apart from a branch switch (drop stale data, show loading).
  private renderedBranchId: string | null = null;

  private getSortedMilestones = (): BranchMilestoneDto[] => {
    const milestones = this.state.revisionState?.milestones || [];
    return [...milestones].sort((a, b) => {
      const aDate = new Date(a.createdAt || "").getTime();
      const bDate = new Date(b.createdAt || "").getTime();
      return bDate - aDate;
    });
  };

  private commitKey = (c: BranchTimelineEntryDto) => `${c.commitId}-${c.commitNo}`;

  private getCurrentCommitId = (): string | undefined => {
    return resolveCurrentCommitId(this.state.revisionState?.headCommitId, this.state.branchTimeline);
  };

  private isCurrentCommit = (c: BranchTimelineEntryDto): boolean => {
    return c.commitId === this.getCurrentCommitId();
  };

  private resolveNodeKind = (
    entry: BranchTimelineEntryDto,
    isCurrent: boolean,
    hasMilestone: boolean
  ): "current" | "merge" | "revert" | "milestone" | "snapshot" | "normal" => {
    if (isCurrent) return "current";
    if (entry.isMergeCommit) return "merge";
    if (entry.isRevertCommit) return "revert";
    if (hasMilestone) return "milestone";
    if (entry.snapshotId) return "snapshot";
    return "normal";
  };

  private milestoneIdsForCommit = (commit: BranchTimelineEntryDto): Set<string> => {
    const ids = new Set<string>();
    if (!commit.snapshotId) return ids;
    for (const m of this.getSortedMilestones()) {
      if (m.snapshotId && m.snapshotId === commit.snapshotId) {
        ids.add(m.id);
      }
    }
    return ids;
  };

  private loadCommitsContext = async () => {
    const branchId: string = this.props.branchId;
    if (!branchId) return;

    // On a branch switch, drop the previous branch's data so the loading state — not stale commits —
    // shows. On a same-branch refresh, keep the current content visible while reloading.
    const isBranchSwitch: boolean = this.renderedBranchId !== null && this.renderedBranchId !== branchId;
    if (isBranchSwitch) {
      this.setState({
        isLoading: true,
        hasLoaded: false,
        loadError: null,
        revisionState: undefined,
        branchTimeline: [],
        selectedCommit: null,
        expandedCommitKeys: []
      });
    } else {
      this.setState({ isLoading: true, loadError: null });
    }

    try {
      const response = await BranchingService.GetCommitsContext(branchId, 100);
      const ctx = response?.data;
      const revisionState = ctx?.head;
      this.renderedBranchId = branchId;
      this.setState({
        isLoading: false,
        hasLoaded: true,
        loadError: null,
        revisionState,
        branchTimeline: revisionState ? (ctx?.commits ?? []) : [],
        selectedCommit: null,
        expandedCommitKeys: []
      });
    } catch (error: any) {
      const errorMessage: string = error?.message || "Failed to load commits.";
      this.renderedBranchId = branchId;
      this.setState({
        isLoading: false,
        hasLoaded: true,
        loadError: errorMessage,
        revisionState: undefined,
        branchTimeline: [],
        selectedCommit: null,
        expandedCommitKeys: []
      });
    }
  };

  private handleSelectCommit = (entry: BranchTimelineEntryDto) => {
    this.setState({ selectedCommit: entry });
  };

  private toggleCommitExpanded = (key: string) => {
    this.setState((s) => ({
      expandedCommitKeys: s.expandedCommitKeys.includes(key)
        ? s.expandedCommitKeys.filter((k) => k !== key)
        : [...s.expandedCommitKeys, key]
    }));
  };

  /** Resolves `true` once the branch has been restored and its history reloaded. */
  private handleRestoreSnapshot = async (snapshotId: string): Promise<boolean> => {
    const revisionState = this.state.revisionState;
    if (!revisionState) return false;

    const payload: RestoreSnapshotRequestDto = {
      snapshotId,
      expectedHeadVersion: revisionState.headVersion
    };

    this.setState({ isLoading: true });
    this.props.setLoading?.(true);
    try {
      const result = await BranchingService.RestoreSnapshot(this.props.branchId, payload);
      if (result?.data) {
        await this.loadCommitsContext();
        this.props.onAfterRevert?.();
        return true;
      }
      return false;
    } catch (error: any) {
      KMainFunctions.displayErrorNotification(error?.message || "Failed to restore checkpoint.");
      return false;
    } finally {
      this.setState({ isLoading: false });
      this.props.setLoading?.(false);
    }
  };

  /** Resolves `true` once the branch has been rewound and its history reloaded. */
  private revertToPoint = async (epochNo: number, targetSeqNo: number): Promise<boolean> => {
    const revisionState = this.state.revisionState;
    if (!revisionState) return false;

    const payload: RevertToPointRequestDto = {
      epochNo,
      targetSeqNo,
      expectedHeadVersion: revisionState.headVersion
    };

    this.setState({ isLoading: true });
    this.props.setLoading?.(true);
    try {
      const result = await BranchingService.RevertToPoint(this.props.branchId, payload);
      if (result?.data) {
        await this.loadCommitsContext();
        this.props.onAfterRevert?.();
        return true;
      }
      return false;
    } catch (error: any) {
      KMainFunctions.displayErrorNotification(
        error?.message || "Failed to rewind branch. A milestone/checkpoint may be required at or before that version."
      );
      return false;
    } finally {
      this.setState({ isLoading: false });
      this.props.setLoading?.(false);
    }
  };

  // Destructive actions (restore/rewind) are gated behind a confirmation modal — see #15. The
  // milestone chips and the per-commit restore button only *request* the action; it runs after
  // the user confirms in confirmPendingRestore.
  private requestRestoreSnapshot = (snapshotId: string, label: string) => {
    this.setState({ pendingRestore: { mode: "restore", snapshotId, label } });
  };

  private handleRestoreFromSelectedCommit = () => {
    const { selectedCommit, revisionState } = this.state;
    if (!selectedCommit || !revisionState) {
      KMainFunctions.displayWarningNotification("Select a commit first.");
      return;
    }
    if (this.isCurrentCommit(selectedCommit)) {
      KMainFunctions.displayInfoNotification("This is already the current commit on the branch.");
      return;
    }
    const label = `commit #${selectedCommit.commitNo}`;
    if (selectedCommit.snapshotId) {
      this.setState({ pendingRestore: { mode: "restore", snapshotId: selectedCommit.snapshotId, label } });
    } else {
      this.setState({
        pendingRestore: {
          mode: "rewind",
          epochNo: revisionState.activeEpochNo,
          targetSeqNo: selectedCommit.commitNo,
          label
        }
      });
    }
  };

  private cancelPendingRestore = () => {
    if (!this.restoreStatus.canStart) return;
    this.setState({ pendingRestore: null });
  };

  /**
   * The confirmation used to close before the restore had even left. It now stays up while the
   * branch is restored, holds its ✓, and closes onto the refreshed history; a refusal stays on
   * its button as "Try again" beside the toast that says why.
   */
  private confirmPendingRestore = async () => {
    const pending = this.state.pendingRestore;
    if (!pending || !this.restoreStatus.canStart) return;
    this.restoreStatus.start();
    const isDone: boolean =
      pending.mode === "restore"
        ? await this.handleRestoreSnapshot(pending.snapshotId)
        : await this.revertToPoint(pending.epochNo, pending.targetSeqNo);
    if (!isDone) {
      this.restoreStatus.fail();
      return;
    }
    this.restoreStatus.succeed((): void => {
      this.setState({ pendingRestore: null });
    });
  };

  render() {
    const { isLoading, hasLoaded, revisionState, selectedCommit, expandedCommitKeys, branchTimeline } = this.state;

    const milestones = this.getSortedMilestones();
    const latest = branchTimeline[0];
    const canRestoreSelected = selectedCommit && !this.isCurrentCommit(selectedCommit);
    const restoreUsesSnapshot = !!selectedCommit?.snapshotId;

    return (
      <div className="branching-revert-timeline">
        <div className="branching-revert-timeline__toolbar">
          <div className="branching-revert-timeline__toolbar-left">
            <KuikaSvg name="git-commit" width={16} height={16} />
            <span className="branching-revert-timeline__toolbar-title">Commit history</span>
            {branchTimeline.length > 0 && (
              <span className="branching-revert-timeline__toolbar-count">{branchTimeline.length}</span>
            )}
          </div>
          <KuikaButton type="secondary" size="sm" onClick={this.loadCommitsContext} disabled={isLoading}>
            <span className="branching-revert-timeline__btn-label">
              <KuikaSvg name="refresh" width={14} height={14} />
              Refresh
            </span>
          </KuikaButton>
        </div>

        {!revisionState && (isLoading || !hasLoaded) && (
          <KuikaSkeletonRegion label="Loading commit history" className="branching-revert-timeline__skeleton">
            <KuikaSkeletonList rows={6} withIcon lines={2} />
          </KuikaSkeletonRegion>
        )}

        {!revisionState && !isLoading && hasLoaded && (
          <div className="branching-revert-timeline__notice" data-tone="warn">
            <KuikaSvg name="alert-triangle" width={16} height={16} />
            <span className="branching-revert-timeline__notice-text">
              {this.state.loadError
                ? "Couldn’t load this branch’s commit history. Check your connection and try again."
                : "No commit history is available for this branch yet."}
            </span>
            <KuikaButton type="secondary" size="sm" onClick={this.loadCommitsContext} disabled={isLoading}>
              <span className="branching-revert-timeline__btn-label">
                <KuikaSvg name="refresh" width={14} height={14} />
                Retry
              </span>
            </KuikaButton>
          </div>
        )}

        {revisionState && (
          <>
            <div className="branching-revert-timeline__status">
              <span className="branching-revert-timeline__status-dot" />
              <span className="branching-revert-timeline__status-main">
                Version <strong>#{revisionState.headVersion}</strong>
                {latest ? (
                  <>
                    {" · latest "}
                    <strong>#{latest.commitNo}</strong>
                    {latest.message ? ` — ${truncateText(latest.message, 36)}` : ""}
                  </>
                ) : (
                  " · no commits yet"
                )}
              </span>
              <span className="branching-revert-timeline__status-meta">
                {branchTimeline.length} commit{branchTimeline.length === 1 ? "" : "s"}
              </span>
            </div>

            {milestones.length > 0 && (
              <div className="branching-revert-timeline__milestones">
                <div className="branching-revert-timeline__section-label">Milestones</div>
                <div className="branching-revert-timeline__milestone-chips">
                  {milestones.map((m) => (
                    <KuikaTooltip
                      key={m.id}
                      content={m.snapshotId ? "Restore branch to this milestone" : "No checkpoint for this milestone"}
                      position="top"
                    >
                      <button
                        type="button"
                        className="branching-revert-timeline__milestone-chip"
                        disabled={!m.snapshotId || isLoading}
                        onClick={() =>
                          m.snapshotId && this.requestRestoreSnapshot(m.snapshotId, m.title || `Milestone ${m.epochNo}`)
                        }
                      >
                        <KuikaSvg name="star" width={12} height={12} />
                        {m.title || `Milestone ${m.epochNo}`}
                      </button>
                    </KuikaTooltip>
                  ))}
                </div>
              </div>
            )}

            <div className="branching-revert-timeline__section-label">All commits</div>
            {branchTimeline.length === 0 ? (
              <div className="branching-revert-timeline__empty">
                <KuikaSvg name="git-commit" width={22} height={22} />
                <span>No commits yet.</span>
              </div>
            ) : (
              <div className="branching-revert-timeline__timeline">
                {branchTimeline.map((entry) => {
                  const key = this.commitKey(entry);
                  const isSelected = selectedCommit?.commitId === entry.commitId;
                  const isCurrent = this.isCurrentCommit(entry);
                  const expanded = expandedCommitKeys.includes(key);
                  const changes = entry.changes || [];
                  const milestoneMatch = this.milestoneIdsForCommit(entry);
                  const showInlineAction = isSelected && canRestoreSelected;
                  const { summary, description: msgDescription } = parseMessageParts(entry.message);
                  const changeSummaryLine = formatChangeSummaryLine(changes);
                  const nodeKind = this.resolveNodeKind(entry, isCurrent, milestoneMatch.size > 0);

                  return (
                    <div
                      key={key}
                      className="branching-revert-timeline__commit"
                      data-selected={isSelected || undefined}
                    >
                      <div className="branching-revert-timeline__commit-rail">
                        <span className="branching-revert-timeline__commit-node" data-kind={nodeKind} />
                      </div>
                      <div className="branching-revert-timeline__commit-body">
                        <button
                          type="button"
                          className={`branching-revert-timeline__commit-main ${OVERFLOW_REVEAL_HOST_CLASS}`}
                          onClick={() => this.handleSelectCommit(entry)}
                        >
                          <div className="branching-revert-timeline__commit-headline">
                            <span className="branching-revert-timeline__commit-no">#{entry.commitNo}</span>
                            <span className="branching-revert-timeline__commit-msg">
                              <KuikaOverflowText>{summary}</KuikaOverflowText>
                            </span>
                            <span className="branching-revert-timeline__commit-time">
                              {entry.createdAt ? formatRelativeTime(new Date(entry.createdAt)) : ""}
                            </span>
                          </div>
                          {msgDescription && (
                            <div className="branching-revert-timeline__commit-desc">
                              {truncateText(msgDescription, 120)}
                            </div>
                          )}
                          {entry.isMergeCommit && entry.sourceBranchName && (
                            <div className="branching-revert-timeline__commit-merge">
                              <KuikaSvg name="git-merge" width={13} height={13} />
                              Merged from <strong>{entry.sourceBranchName}</strong>
                              {changes.length > 0 && <> · {changeSummaryLine}</>}
                            </div>
                          )}
                          <div className="branching-revert-timeline__commit-badges">
                            {isCurrent && (
                              <span className="branching-revert-timeline__badge" data-kind="current">
                                Current
                              </span>
                            )}
                            {entry.isMergeCommit && (
                              <span className="branching-revert-timeline__badge" data-kind="merge">
                                Merge
                              </span>
                            )}
                            {entry.isRevertCommit && (
                              <span className="branching-revert-timeline__badge" data-kind="revert">
                                Undo
                              </span>
                            )}
                            {milestoneMatch.size > 0 && (
                              <span className="branching-revert-timeline__badge" data-kind="milestone">
                                Milestone
                              </span>
                            )}
                            {entry.snapshotId && (
                              <span className="branching-revert-timeline__badge" data-kind="snapshot">
                                Checkpoint
                              </span>
                            )}
                            {entry.author && (
                              <span className="branching-revert-timeline__commit-author" title={entry.author}>
                                {truncateText(entry.author, 20)}
                              </span>
                            )}
                          </div>
                        </button>

                        <button
                          type="button"
                          className="branching-revert-timeline__commit-expand"
                          data-expanded={expanded || undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            this.toggleCommitExpanded(key);
                          }}
                        >
                          <KuikaSvg
                            name="chevron-down"
                            type="arrows"
                            width={13}
                            height={13}
                            className="branching-revert-timeline__chevron"
                          />
                          {changes.length} change{changes.length === 1 ? "" : "s"}
                        </button>

                        {expanded && (
                          <ul className="branching-revert-timeline__change-list">
                            {changes.length === 0 ? (
                              <li className="branching-revert-timeline__change-item">
                                No entity-level details recorded.
                              </li>
                            ) : (
                              changes.map((ch, idx) => {
                                const line = formatChangeSummary(ch);
                                const long = (ch.summaryJson && ch.summaryJson.length > 120) || line.length > 100;
                                return (
                                  <li key={`${key}-ch-${idx}`} className="branching-revert-timeline__change-item">
                                    {long ? (
                                      <KuikaTooltip content={ch.summaryJson || line} position="top">
                                        <span>{truncateText(line, 100)}</span>
                                      </KuikaTooltip>
                                    ) : (
                                      <span>{line}</span>
                                    )}
                                  </li>
                                );
                              })
                            )}
                          </ul>
                        )}

                        {showInlineAction && (
                          <div className="branching-revert-timeline__restore">
                            <div className="branching-revert-timeline__restore-note">
                              <KuikaSvg name="alert-triangle" width={15} height={15} />
                              <span>
                                {restoreUsesSnapshot
                                  ? "The whole branch will be restored to this commit’s saved checkpoint."
                                  : "The branch will be rewound to at or before this version (requires a checkpoint at or before this point)."}
                              </span>
                            </div>
                            <KuikaButton
                              type="primary"
                              size="sm"
                              onClick={this.handleRestoreFromSelectedCommit}
                              disabled={isLoading}
                            >
                              <span className="branching-revert-timeline__btn-label">
                                <KuikaSvg name="restore" width={14} height={14} />
                                {restoreUsesSnapshot ? "Restore to this commit" : "Rewind to this commit"}
                              </span>
                            </KuikaButton>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <RestoreCommitModal
          showModal={!!this.state.pendingRestore}
          mode={this.state.pendingRestore?.mode ?? "restore"}
          targetLabel={this.state.pendingRestore?.label}
          inProgress={isLoading}
          status={this.state.restoreStatus}
          onClose={this.cancelPendingRestore}
          onConfirm={this.confirmPendingRestore}
        />
      </div>
    );
  }
}

export default BranchingRevertTimeline;
